"use client";

import { Icon } from "@/components/ui/Icon";
import { AddressSuggestions } from "./AddressSuggestions";
import { AddressInputProps, AddressSuggestion } from "./types";
import { useEffect, useRef, useState } from "react";
import { POINTS_INTERET } from "./constants";
import { tr } from "@/config/localeText";
import { RESERVATION_UI } from "./reservationFormUi";

export const AddressInput = ({
  id,
  name,
  value,
  onChange,
  onSelect,
  suggestions,
  showSuggestions,
  setShowSuggestions,
  isLoading,
  label,
  placeholder,
  onFilterByType,
  showQuickFilters = false,
  flightNumber,
  trainNumber,
  onFlightNumberChange,
  onTrainNumberChange,
  selectedType,
  onSelectedTypeChange,
  locale = "fr",
}: AddressInputProps) => {
  const t = tr(RESERVATION_UI, locale).addressInput;
  const [isFocused, setIsFocused] = useState(false);
  const [activePoiType, setActivePoiType] = useState<
    "gare" | "aeroport" | null
  >(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearBlurTimeout = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  // Effet pour détecter automatiquement le type en fonction de la valeur
  useEffect(() => {
    if (value && !selectedType) {
      if (
        value.toLowerCase().includes("aéroport") ||
        value.toLowerCase().includes("mérignac")
      ) {
        onSelectedTypeChange?.("aeroport");
      } else if (value.toLowerCase().includes("gare")) {
        onSelectedTypeChange?.("gare");
      }
    }
  }, [value, selectedType, onSelectedTypeChange]);

  // Nettoyage du timeout au démontage
  useEffect(() => {
    return () => {
      clearBlurTimeout();
    };
  }, []);

  // Fermer les suggestions sur clic en dehors du composant
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!showSuggestions) return;
      const container = containerRef.current;
      if (!container) return;
      if (container.contains(event.target as Node)) return;

      clearBlurTimeout();
      setShowSuggestions(false);
      setIsFocused(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [showSuggestions, setShowSuggestions]);

  // Fonction pour formater la valeur affichée
  const getDisplayValue = () => {
    const suggestion = suggestions.find((s) => s.label === value);
    if (suggestion?.isPOI) {
      return suggestion.name;
    }
    return value;
  };

  const handleFilterClick = (
    type: "gare" | "aeroport" | "destination" | "hotel",
  ) => {
    setActivePoiType(null);
    onSelectedTypeChange?.(type);
    if (onFilterByType) {
      onFilterByType(type);
    }
  };

  // Fonction pour gérer la sélection d'une adresse
  const handleSelect = (suggestion: AddressSuggestion) => {
    if (suggestion.isPOI) {
      const poiType: "gare" | "aeroport" | "destination" | "hotel" | null =
        suggestion.type === "gare" ||
        suggestion.type === "aeroport" ||
        suggestion.type === "destination" ||
        suggestion.type === "hotel"
          ? (suggestion.type as "gare" | "aeroport" | "destination" | "hotel")
          : null;
      const transportPoiType =
        poiType === "gare" || poiType === "aeroport" ? poiType : null;
      setActivePoiType(transportPoiType);
      onSelectedTypeChange?.(poiType);
    } else {
      // Adresse libre → ne pas garder les champs hors focus
      setActivePoiType(null);
      onSelectedTypeChange?.(null);
    }
    onSelect(suggestion);
  };

  const showAirportDetails =
    (isFocused && selectedType === "aeroport") || activePoiType === "aeroport";
  const showTrainDetails =
    (isFocused && selectedType === "gare") || activePoiType === "gare";

  return (
    <div ref={containerRef} className="w-full px-2 md:w-1/2">
      <label htmlFor={id} className="mb-2 block font-medium text-black">
        {label}
      </label>
      <div className="relative">
        <Icon
          icon="ph:map-pin"
          className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gold-700"
        />
        <input
          type="text"
          id={id}
          name={name}
          value={getDisplayValue()}
          onChange={(e) => {
            if (activePoiType) setActivePoiType(null);
            onChange(e);
          }}
          onFocus={() => {
            clearBlurTimeout();
            setIsFocused(true);
            if (value.length >= 3) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
            clearBlurTimeout();
            blurTimeoutRef.current = setTimeout(() => {
              setShowSuggestions(false);
            }, 120);
          }}
          onClick={(e) => {
            e.stopPropagation();
            clearBlurTimeout();
            if (value.length >= 3) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          className="h-12 w-full rounded-lg border border-black/15 bg-white py-3 pe-4 ps-10 text-black outline-none transition-all placeholder:text-black/40 focus:border-gold-600 focus:ring-1 focus:ring-gold-300"
          autoComplete="off"
        />
        {isLoading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <Icon
              icon="ph:spinner"
              className="h-5 w-5 animate-spin text-gold-700"
            />
          </div>
        )}
        <AddressSuggestions
          suggestions={suggestions}
          onSelect={handleSelect}
          isLoading={isLoading}
          show={showSuggestions}
          locale={locale}
        />
      </div>

      {showQuickFilters && onFilterByType && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleFilterClick("gare")}
            className="flex items-center gap-1 rounded-full bg-gold-100 px-3 py-1 text-sm text-gold-800 transition-colors hover:bg-gold-200"
          >
            <Icon icon="ph:train" className="h-4 w-4" />
            <span>{t.stations}</span>
          </button>
          <button
            type="button"
            onClick={() => handleFilterClick("aeroport")}
            className="flex items-center gap-1 rounded-full bg-gold-100 px-3 py-1 text-sm text-gold-800 transition-colors hover:bg-gold-200"
          >
            <Icon icon="ph:airplane" className="h-4 w-4" />
            <span>{t.airports}</span>
          </button>
          {POINTS_INTERET.destinations.length > 0 && (
            <button
              type="button"
              onClick={() => handleFilterClick("destination")}
              className="flex items-center gap-1 rounded-full bg-gold-100 px-3 py-1 text-sm text-gold-800 transition-colors hover:bg-gold-200"
            >
              <Icon icon="ph:map-pin" className="h-4 w-4" />
              <span>{t.destinations}</span>
            </button>
          )}
          {POINTS_INTERET.hotels.length > 0 && (
            <button
              type="button"
              onClick={() => handleFilterClick("hotel")}
              className="flex items-center gap-1 rounded-full bg-gold-100 px-3 py-1 text-sm text-gold-800 transition-colors hover:bg-gold-200"
            >
              <Icon icon="ph:buildings" className="h-4 w-4" />
              <span>{t.hotels}</span>
            </button>
          )}
        </div>
      )}

      {showAirportDetails && (
        <div className="mt-4">
          <div>
            <label
              htmlFor={`${id}-flight-number`}
              className="mb-2 block text-sm font-medium text-black/70"
            >
              {t.flightNumber}
            </label>
            <input
              type="text"
              id={`${id}-flight-number`}
              value={flightNumber}
              onChange={(e) => onFlightNumberChange?.(e.target.value)}
              placeholder={t.flightExample}
              className="h-12 w-full rounded-lg border border-black/15 bg-white px-4 py-2 text-black outline-none transition-all placeholder:text-black/40 focus:border-gold-600 focus:ring-1 focus:ring-gold-300"
            />
          </div>
        </div>
      )}

      {showTrainDetails && (
        <div className="mt-4">
          <div>
            <label
              htmlFor={`${id}-train-number`}
              className="mb-2 block text-sm font-medium text-black/70"
            >
              {t.trainNumber}
            </label>
            <input
              type="text"
              id={`${id}-train-number`}
              value={trainNumber}
              onChange={(e) => onTrainNumberChange?.(e.target.value)}
              placeholder={t.trainExample}
              className="h-12 w-full rounded-lg border border-black/15 bg-white px-4 py-2 text-black outline-none transition-all placeholder:text-black/40 focus:border-gold-600 focus:ring-1 focus:ring-gold-300"
            />
          </div>
        </div>
      )}
    </div>
  );
};
